import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getLastRead, getRecentReads, type BibleRecentRead } from '@/lib/bible-progress-store';
import { CANONICAL_SLUG_TO_NAME, getChapter } from '@/lib/bible/active';
import { chapterVerseTexts } from '@/lib/scripture-anchor';
import { useBibleBootstrapStore } from '@/lib/bsb-progress-store';
import { formatRelativeTime } from '@/lib/prayer-utils';
import { useNavLayer, useNavStack } from '@/hooks/useNavLayer';
import SheetShell from '@/components/SheetShell';
import { useReadingAccess } from '@/lib/reading-access';
import styles from './ContinueReadingCard.module.css';

interface RecentView {
  bookSlug: string;
  bookName: string;
  chapter: number;
  timestamp: number;
  verse?: number;
}

interface ContinueReadingCardProps {
  // Fires (before navigation) when the user taps the card or a history row,
  // so the index can stamp a `neutral` restoration snapshot (navigation
  // chunk C, README §6.2). Optional so the card stays decoupled from the
  // snapshot store.
  onNavigate?: () => void;
  // Fires once a load has settled, whatever it found (a card, no history, a
  // restricted reader, a failed read), and at once while a Bible download
  // keeps the card from loading, so the index can hold the content below the
  // card until the card has its final size.
  onSettled?: () => void;
}

function toView(row: BibleRecentRead): RecentView | null {
  const bookName = CANONICAL_SLUG_TO_NAME.get(row.bookSlug);
  if (!bookName) return null;
  return {
    bookSlug: row.bookSlug,
    bookName,
    chapter: row.chapter,
    timestamp: row.timestamp,
    verse: row.verse,
  };
}

type RecentGroup = 'today' | 'yesterday' | 'earlier';

function groupFor(timestamp: number, now: number): RecentGroup {
  const day = (ts: number) => {
    const d = new Date(ts);
    d.setHours(0, 0, 0, 0);
    return d.getTime();
  };
  const diff = day(now) - day(timestamp);
  if (diff <= 0) return 'today';
  if (diff <= 86_400_000) return 'yesterday';
  return 'earlier';
}

const GROUP_ORDER: RecentGroup[] = ['today', 'yesterday', 'earlier'];
const GROUP_LABEL_KEY: Record<RecentGroup, string> = {
  today: 'bible.recentsToday',
  yesterday: 'bible.recentsYesterday',
  earlier: 'bible.recentsEarlier',
};

/**
 * "Where you left off" hero for the Bible index: the destination, one line of
 * the chapter itself, a filled "Continue reading" button, and an outlined
 * "Reading history" button that opens a slide-up sheet of recent chapters
 * grouped Today / Yesterday / Earlier.
 *
 * ONE real link continues reading: the filled button. Its ::after is stretched
 * over the whole hero (CSS), so a tap anywhere on the hero continues while
 * keyboard and screen-reader users meet a single link. The history button sits
 * above that stretched area. Do not wrap the hero in a second link or button
 * (nested interactive content, and a duplicate tab stop).
 *
 * The verse line is read from the LOCAL Bible copy only (getChapter is an IDB
 * read, never a fetch) and is simply omitted when the book is not on the
 * device yet. It prefers the verse the reader was near, else the chapter's
 * first verse.
 *
 * The sheet is the `bible-recents` NavStack layer (rule #78, AB pattern:
 * registered from a stable owner that mounts closed; plain state-clear
 * close, no save-bearing state). Row taps navigate via
 * nav.resetToBaseThenNavigate so the layer's history entry unwinds before
 * the route push (a plain <Link> inside an open layer would desync the
 * synthetic depth).
 */
export function ContinueReadingCard({ onNavigate, onSettled }: ContinueReadingCardProps) {
  const { t } = useTranslation();
  const reading = useReadingAccess();
  const nav = useNavStack();
  // Successful same-owner admission must not remove the card or close history
  // while its fresh local reads settle. Owner changes still fence old content.
  const accessKey = reading.ownerId ?? '';
  const [loadedAccess, setLoadedAccess] = useState<string | null>(null);
  const [view, setView] = useState<RecentView | null>(null);
  const [others, setOthers] = useState<RecentView[]>([]);
  // Keyed to the chapter it was read for, so a line resolved for an older
  // last-read can never render under a newer destination.
  const [verseLine, setVerseLine] = useState<{ key: string; text: string } | null>(null);
  const [sheetAccess, setSheetAccess] = useState<string | null>(null);
  const sheetOpen = sheetAccess === accessKey && (!reading.restricted || reading.local !== null);
  const setSheetOpen = (open: boolean) => setSheetAccess(open ? accessKey : null);
  // Grouping anchor: captured at load time (not render) so render stays pure;
  // the list reloads on every sheet open, so the anchor is always fresh.
  const [loadedAt, setLoadedAt] = useState(0);

  const zIndex = useNavLayer('bible-recents', sheetOpen, () => setSheetOpen(false));

  // Kept in a ref so a caller's new function identity never reloads the card.
  const onSettledRef = useRef(onSettled);
  useEffect(() => {
    onSettledRef.current = onSettled;
  }, [onSettled]);

  const load = useCallback(() => {
    let cancelled = false;
    if (reading.restricted && !reading.local) {
      onSettledRef.current?.();
      return () => { cancelled = true; };
    }
    void Promise.all([getLastRead(), getRecentReads()])
      .then(async ([row, recents]) => {
        if (cancelled) return;
        if (!row) { setView(null); setOthers([]); return; }
        const last = toView({ ...row });
        if (!last) return;
        const lastKey = `${row.bookSlug}:${row.chapter}`;
        const nearVerse = recents.find(
          (e) => `${e.bookSlug}:${e.chapter}` === lastKey,
        )?.verse;
        // The verse line is read before the card shows (a local read, a few
        // ms), so the card appears in one piece instead of growing a 57px
        // line under itself a frame later and pushing the page down again.
        const blocks = await getChapter(row.bookSlug, row.chapter).catch(() => null);
        if (cancelled) return;
        setLoadedAccess(accessKey);
        setView(last);
        setOthers(
          recents
            .filter((e) => `${e.bookSlug}:${e.chapter}` !== lastKey)
            .map(toView)
            .filter((e): e is RecentView => e !== null),
        );
        setLoadedAt(Date.now());
        if (!blocks) return;
        const texts = chapterVerseTexts(blocks);
        if (texts.size === 0) return;
        const verse =
          nearVerse !== undefined && texts.has(nearVerse)
            ? nearVerse
            : Math.min(...texts.keys());
        const text = texts.get(verse);
        if (text) setVerseLine({ key: lastKey, text });
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) onSettledRef.current?.();
      });
    return () => {
      cancelled = true;
    };
  }, [reading.restricted, reading.local, accessKey]);

  // Load on mount AND refresh whenever the sheet opens (the old card loaded
  // once on mount only and could go stale within a session). Also reload when
  // a Bible download finishes: the verse line needs the book on this device,
  // and a first-ever download can complete after the first load ran.
  const isBibleBootstrapping = useBibleBootstrapStore((s) => s.isBibleBootstrapping);
  useEffect(() => {
    if (isBibleBootstrapping) {
      // Nothing loads while a download runs, so release the index's hold now.
      onSettledRef.current?.();
      return;
    }
    return load();
  }, [isBibleBootstrapping, load]);
  useEffect(() => {
    if (sheetOpen) return load();
  }, [sheetOpen, load]);

  // Host-owned Escape while the sheet is open (AB precedent: SheetShell has
  // no internal Escape handler). Routes through requestClose so the history
  // entry unwinds like backdrop / drag / browser back.
  useEffect(() => {
    if (!sheetOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') nav.requestClose('bible-recents');
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [sheetOpen, nav]);

  if (!view || loadedAccess !== accessKey || (reading.restricted && !reading.local)) return null;

  const ariaLabel = t('bible.continueReadingAria', {
    book: view.bookName,
    chapter: view.chapter,
  });
  const timeAgo = formatRelativeTime(view.timestamp, t);

  const openEntry = (entry: RecentView) => {
    onNavigate?.();
    nav.resetToBaseThenNavigate(`/bible/${entry.bookSlug}/${entry.chapter}`);
  };

  const grouped = GROUP_ORDER.map((group) => ({
    group,
    entries: others.filter((e) => groupFor(e.timestamp, loadedAt) === group),
  })).filter((g) => g.entries.length > 0);

  const renderEntry = (entry: RecentView) => {
    const time = formatRelativeTime(entry.timestamp, t);
    const verseNote =
      entry.verse !== undefined
        ? ` · ${t('bible.recentsNearVerse', { verse: entry.verse })}`
        : '';
    return (
      <li key={`${entry.bookSlug}:${entry.chapter}`}>
        <button
          type="button"
          className={styles.entryRow}
          aria-label={t('bible.recentsEntryAria', {
            book: entry.bookName,
            chapter: entry.chapter,
            time,
          })}
          onClick={() => openEntry(entry)}
        >
          <span className={styles.entryMain}>
            {entry.bookName} {entry.chapter}
          </span>
          <span className={styles.entryMeta}>
            {time}
            {verseNote}
          </span>
        </button>
      </li>
    );
  };

  const verseText =
    verseLine && verseLine.key === `${view.bookSlug}:${view.chapter}`
      ? verseLine.text
      : null;

  return (
    <div className={styles.block}>
      <div className={styles.hero}>
        <svg
          className={styles.ribbon}
          width="22"
          height="44"
          viewBox="0 0 22 44"
          aria-hidden="true"
          focusable="false"
        >
          <path d="M0 0h22v44L11 35 0 44z" fill="#c4a265" />
          <path d="M0 0h22v5H0z" fill="#a8884f" />
        </svg>
        <p className={styles.eyebrow}>
          <span className={styles.eyebrowLabel}>{t('bible.continueLeftOff')}</span>
          {` · ${timeAgo}`}
        </p>
        <p className={styles.destination}>
          {view.bookName} {view.chapter}
        </p>
        {verseText && <p className={styles.verse}>{verseText}</p>}
        <div className={styles.actions}>
          <Link
            to={`/bible/${view.bookSlug}/${view.chapter}`}
            className={styles.continueBtn}
            aria-label={ariaLabel}
            onClick={onNavigate}
          >
            {t('bible.continueReading')}
          </Link>
          {others.length > 0 && (
            <button
              type="button"
              className={styles.historyBtn}
              aria-haspopup="dialog"
              aria-expanded={sheetOpen}
              aria-label={t('bible.recentsRowAria')}
              onClick={() => setSheetOpen(true)}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
                focusable="false"
              >
                <circle cx="12" cy="12" r="9" />
                <polyline points="12 7 12 12 15.5 14" />
              </svg>
              <span>{t('bible.recentsRowLabel')}</span>
            </button>
          )}
        </div>
      </div>
      {sheetOpen && (
        <SheetShell
          ariaLabel={t('bible.recentsTitle')}
          onBackdrop={() => nav.requestClose('bible-recents')}
          closeLabel={t('bible.recentsCloseAria')}
          zIndex={zIndex}
          dragToDismiss
        >
          <div className={styles.recentsBody}>
            <h2 className={styles.recentsTitle} data-sheet-drag>
              {t('bible.recentsTitle')}
            </h2>
            {grouped.map(({ group, entries }) => (
              <section key={group} className={styles.recentsGroup}>
                <h3 className={styles.groupLabel}>{t(GROUP_LABEL_KEY[group])}</h3>
                <ul className={styles.entryList}>{entries.map(renderEntry)}</ul>
              </section>
            ))}
          </div>
        </SheetShell>
      )}
    </div>
  );
}
